/**
 * Konfigurasi landing page MGMX (Decision 32, desain varian A per Decision 33).
 *
 * Nilai bisnis TIDAK di-hardcode di komponen: nomor WhatsApp vendor diambil dari env
 * (di-inline Next saat build) dengan default di bawah.
 *   - NEXT_PUBLIC_LANDING_WA           : nomor WA vendor (0813…/+62 813…/62813…; dinormalisasi ke 62…, default 6281357887884)
 *
 * Demo gratis (login otomatis akun demo) DIHAPUS 2026-09-29 — pak Kristian: demo tidak dibuka untuk publik,
 * hanya demo dipandu tim (form "Minta Demo"). Kredensial demo tidak lagi ada di bundle FE.
 */

const DEFAULT_WA_VENDOR = '6281357887884';

/**
 * Normalisasi nomor WhatsApp Indonesia ke format 62xxxxxxxxxx.
 * Menerima awalan 0, 62, +62, 0062, atau langsung 8. Karakter non-digit dibuang.
 * Nomor nasional (setelah 62) wajib 8–13 digit dan tidak diawali 0 → total 10–15 digit.
 * Mengembalikan null bila tidak valid.
 */
export function normalizeWaNumber(raw: string): string | null {
    const digits = (raw || '').replace(/\D/g, '');
    let national: string;
    if (digits.startsWith('0062')) {
        national = digits.slice(4);
    } else if (digits.startsWith('62')) {
        national = digits.slice(2);
    } else if (digits.startsWith('0')) {
        national = digits.slice(1);
    } else if (digits.startsWith('8')) {
        national = digits;
    } else {
        return null;
    }
    // "+62 0812..." → buang 0 setelah kode negara
    national = national.replace(/^0+/, '');
    if (!/^[1-9]\d{7,12}$/.test(national)) return null;
    return `62${national}`;
}

/**
 * Normalisasi input nomor WhatsApp dari form (kolom dengan kotak prefiks "+62").
 * Kotak "+62" mengajak pengunjung mengetik nomor nasional tanpa 0, jadi awalan "62" TIDAK
 * selalu kode negara: kode area 062x (mis. Pematangsiantar 0622) diketik "622 …".
 *   - diawali "+" / "00"              → format internasional; hanya kode negara 62 yang diterima
 *                                       (+81/+82/+86… ditolak, bukan diubah jadi nomor Indonesia)
 *   - diawali "0", "8", "628", "6208" → format lokal/seluler yang umum (normalizeWaNumber)
 *   - selain itu                      → nomor nasional tanpa 0 → diberi awalan "0"
 * Mengembalikan 62xxxxxxxxxx atau null bila tidak valid.
 */
export function normalizeWaInput(raw: string): string | null {
    const trimmed = (raw || '').trim();
    if (/^(\+|00)/.test(trimmed)) {
        const intl = trimmed.replace(/\D/g, '').replace(/^00/, '');
        return intl.startsWith('62') ? normalizeWaNumber(intl) : null;
    }
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return null;
    // "628…" / "6208…" = kode negara + nomor seluler (pengunjung mengabaikan kotak +62)
    if (digits.startsWith('0') || digits.startsWith('8') || /^620?8/.test(digits)) {
        return normalizeWaNumber(digits);
    }
    return normalizeWaNumber(`0${digits}`);
}

/**
 * Nomor WA vendor, hanya digit, format internasional (62xxxxxxxxxx).
 * Env boleh diisi format lokal/internasional apa pun ("0813-5788-7884", "+62 813…");
 * dinormalisasi supaya link wa.me selalu valid. Nilai tidak valid → default.
 */
export const WA_VENDOR: string = normalizeWaNumber(process.env.NEXT_PUBLIC_LANDING_WA || '') ?? DEFAULT_WA_VENDOR;

/** 6281357887884 → 0813-5788-7884 (format lokal untuk ditampilkan). */
function toLocalDisplay(intl: string): string {
    const local = intl.startsWith('62') ? `0${intl.slice(2)}` : intl;
    return [local.slice(0, 4), local.slice(4, 8), local.slice(8)].filter(Boolean).join('-');
}

/**
 * Nomor WA vendor untuk ditampilkan ke pengunjung. Diturunkan dari WA_VENDOR supaya
 * tidak pernah beda dengan nomor tujuan link; default → '0813-5788-7884'.
 */
export const WA_VENDOR_DISPLAY: string = toLocalDisplay(WA_VENDOR);

export const CONTACT = {
    alamat1: 'Jl. Kertajaya Indah VIII No.17 G415, RT.001/RW.09, Manyar Sabrangan, Kec. Mulyorejo',
    alamat2: 'Surabaya, Jawa Timur 60116',
    telp: '031-5932608',
    telpHref: 'tel:+62315932608',
    whatsapp: WA_VENDOR_DISPLAY,
} as const;

/** Paket langganan yang tampil di section "Paket" landing. */
export interface Paket {
    kode: string;
    nama: string;
    tagline: string;
    /** Harga per bulan (rupiah). null → tampil "Hubungi kami". */
    hargaBulan: number | null;
    fitur: readonly string[];
    /** "Keuntungan yang Anda Dapatkan" (training, garansi, bonus). */
    keuntungan?: readonly string[];
    unggulan?: boolean;
}

/** Minimal masa berlangganan (bulan). */
export const MIN_BERLANGGANAN_BULAN = 6;

const KEUNTUNGAN_DASAR = [
    'Garansi online 1 bulan*',
    'Free Company Profile (website informasi perusahaan)',
] as const;

/**
 * Paket FINAL dari tabel harga pak Kristian (2026-09-29): Lite / Pro / Premium, tanpa Enterprise.
 * Fitur & keuntungan verbatim dari tabel. Tagline = tulisan AI (tabel tidak punya tagline).
 * Mode SaaS: nama & harga tetap diambil dari master_plans; fitur/keuntungan dari sini (dicocokkan per kode).
 */
export const PAKET: readonly Paket[] = [
    {
        kode: 'LITE',
        nama: 'MGMX Lite',
        tagline: 'Untuk usaha yang mulai mengelola stok, pembelian, dan penjualan.',
        hargaBulan: 150000,
        fitur: [
            'Stock + Gudang',
            'Multi Gudang + Satuan',
            'Pembelian + PO',
            'Penjualan + SO + POS',
            'Kas + Bank',
            'Export laporan ke Excel',
            'Pembatasan Hak Akses',
            '1 User Lisensi',
        ],
        keuntungan: ['Free training 3 jam*', ...KEUNTUNGAN_DASAR],
    },
    {
        kode: 'PRO',
        nama: 'MGMX Pro',
        tagline: 'Tambah pencatatan hutang dan piutang untuk bisnis yang berkembang.',
        hargaBulan: 200000,
        fitur: [
            'Stock + Gudang',
            'Multi Gudang + Satuan',
            'Pembelian + PO',
            'Penjualan + SO + POS',
            'Kas + Bank',
            'Hutang + Piutang',
            'Export laporan ke Excel',
            'Pembatasan Hak Akses',
            '2 User Lisensi',
        ],
        keuntungan: ['Free training 3 jam*', ...KEUNTUNGAN_DASAR],
        unggulan: true,
    },
    {
        kode: 'PREMIUM',
        nama: 'MGMX Premium',
        tagline: 'Paling lengkap, termasuk akuntansi.',
        hargaBulan: 300000,
        fitur: [
            'Stock + Gudang',
            'Multi Gudang + Satuan',
            'Pembelian + PO',
            'Penjualan + SO + POS',
            'Kas + Bank',
            'Hutang + Piutang',
            'Export laporan ke Excel',
            'Pembatasan Hak Akses',
            '2 User Lisensi',
            'Akuntansi',
        ],
        keuntungan: ['Free training 6 jam*', ...KEUNTUNGAN_DASAR],
    },
];

/** Penambahan user & add-on (tabel harga pak Kristian 2026-09-29). Informasi harga — belum bisa dibeli online. */
export interface AddOn {
    nama: string;
    ket: string;
    hargaBulan: number;
}

export const ADDONS: readonly AddOn[] = [
    { nama: 'User', ket: 'User aktif yang bisa memakai program secara bersamaan', hargaBulan: 15000 },
    { nama: 'Aset', ket: 'Mencatat dan menghasilkan perhitungan atas penyusutan per periode', hargaBulan: 300000 },
    { nama: 'Coretax', ket: 'Membantu pelaporan ke Coretax dengan export Excel dari program dan diupload ke Coretax', hargaBulan: 300000 },
    { nama: 'Serial Number', ket: 'Mencatat nomor seri atau kode khusus yang dapat digunakan untuk mengidentifikasi tiap kode barang', hargaBulan: 300000 },
];

/**
 * Mode landing (env NEXT_PUBLIC_LANDING_MODE):
 *  - 'wa'   (default, MGMX biasa): CTA paket → WhatsApp, harga dari PAKET di atas
 *  - 'saas' (MGMX SaaS)          : harga dari GET /api/public/plans (master_plans), CTA paket →
 *                                   /signup, tambah tombol "Coba Gratis" → /trial
 */
export const LANDING_MODE: 'wa' | 'saas' = process.env.NEXT_PUBLIC_LANDING_MODE === 'saas' ? 'saas' : 'wa';

/** Masa free trial yang ditampilkan (hari). Default 7 (keputusan pak Kristian 2026-09-29, sebelumnya 14). */
export const TRIAL_DAYS: number = (() => {
    const n = parseInt(process.env.NEXT_PUBLIC_TRIAL_DAYS || '', 10);
    return Number.isFinite(n) && n > 0 ? n : 7;
})();

/** Durasi default saat masuk /signup dari landing (bulan). */
export const SIGNUP_DEFAULT_DURATION = 6;

export function buildSignupUrl(kode: string): string {
    return `/signup?plan=${encodeURIComponent(kode)}&dur=${SIGNUP_DEFAULT_DURATION}`;
}

/** Paket dari API SaaS (GET /api/public/plans). */
export interface PublicPlan {
    code: string;
    name: string;
    price_6_months: number;
    price_12_months: number;
    self_signup: boolean;
}

/**
 * Gabungkan paket dari DB (nama, harga, urutan, aktif) dengan tagline/fitur dari PAKET (dicocokkan per kode).
 * Harga per bulan = harga 6 bulan / 6. Paket tanpa self-signup → "Hubungi kami".
 */
export function mergePublicPlans(rows: readonly PublicPlan[]): Paket[] {
    return rows.map((r) => {
        const kode = r.code.toUpperCase();
        const cfg = PAKET.find((p) => p.kode === kode);
        return {
            kode,
            nama: r.name,
            tagline: cfg?.tagline ?? '',
            hargaBulan: r.self_signup ? Math.round(r.price_6_months / SIGNUP_DEFAULT_DURATION) : null,
            fitur: cfg?.fitur ?? [],
            keuntungan: cfg?.keuntungan,
            unggulan: cfg?.unggulan,
        };
    });
}

/** Format rupiah tanpa desimal: 499000 → "Rp 499.000". */
export function formatRupiah(n: number): string {
    // Tanpa toLocaleString: hasil SSR & klien dijamin sama (tidak tergantung ICU/locale)
    return `Rp ${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

/** Pesan WhatsApp saat pengunjung memilih paket. */
export function buildPaketMessage(namaPaket: string): string {
    return `Halo tim MGMX, saya tertarik dengan paket ${namaPaket}. Mohon info lebih lanjut.`;
}

/** Pilihan chip "Modul yang paling dibutuhkan" (boleh lebih dari satu, opsional). */
export const MODUL_OPTIONS: readonly string[] = [
    'General Ledger',
    'Kas & Bank',
    'Hutang & Piutang',
    'Inventory',
    'Pembelian',
    'Penjualan',
];

/**
 * Testimoni pelanggan. WAJIB kutipan asli yang sudah diizinkan pemberinya.
 * null → blok testimoni tidak dirender (mockup hanya berisi contoh, bukan kutipan nyata).
 */
export const TESTIMONI: { kutipan: string; sumber: string } | null = null;

/** Tampilan nomor ternormalisasi: 6281234567890 → +62 812-3456-7890. */
export function formatWaNumber(normalized: string): string {
    const national = normalized.startsWith('62') ? normalized.slice(2) : normalized;
    const parts = [national.slice(0, 3), national.slice(3, 7), national.slice(7)].filter(Boolean);
    return `+62 ${parts.join('-')}`;
}

/** URL wa.me dengan pesan terisi. */
export function buildWaUrl(message: string, number: string = WA_VENDOR): string {
    return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

/** Data form Minta Demo yang sudah divalidasi. Field opsional kosong = tidak dikirim. */
export interface DemoRequestData {
    nama: string;
    perusahaan: string;
    /** Nomor ternormalisasi 62xxxxxxxxxx (hasil normalizeWaNumber). */
    waNormalized: string;
    email?: string;
    modul?: readonly string[];
}

/** Pesan permintaan demo yang dikirim pengunjung ke vendor lewat WhatsApp. */
export function buildDemoRequestMessage(data: DemoRequestData): string {
    const lines = [
        'Halo tim MGMX, saya ingin minta demo MGMX Web.',
        `Nama: ${data.nama}`,
        `Perusahaan: ${data.perusahaan}`,
        `No. WhatsApp: ${formatWaNumber(data.waNormalized)}`,
    ];
    if (data.email) lines.push(`Email: ${data.email}`);
    if (data.modul && data.modul.length > 0) lines.push(`Modul yang dibutuhkan: ${data.modul.join(', ')}`);
    return lines.join('\n');
}

/** Pesan pembuka untuk link WhatsApp di footer. */
export const WA_CHAT_MESSAGE = 'Halo tim MGMX, saya ingin bertanya tentang MGMX Web.';
